import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaGraduationCap, FaWallet, FaHistory } from 'react-icons/fa';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SupportPopup from '../../components/common/SupportPopup';
import API_BASE_URL from '../../config/api';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    if (!user || !user.id) { navigate('/login'); return; }
    const fetchData = async () => {
      try {
        const enRes = await fetch(`${API_BASE_URL}/get_enrolled_months.php?user_id=${user.id}`);
        const enData = await enRes.json();
        if (enData.status === 'success') setEnrollments(enData.data);
        const payRes = await fetch(`${API_BASE_URL}/get_my_payments.php?user_id=${user.id}`);
        const payData = await payRes.json();
        if (payData.status === 'success') setRecentPayments(payData.data);
        const actRes = await fetch(`${API_BASE_URL}/get_my_activity.php?user_id=${user.id}`);
        const actData = await actRes.json();
        if (actData.status === 'success') setRecentActivity(actData.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id]);

  const COLOR_SCHEMES = [
    { bg: 'bg-blue-600', from: 'from-blue-600', to: 'to-indigo-800', light: 'bg-blue-50', text: 'text-blue-600', shadow: 'shadow-blue-200' },
    { bg: 'bg-emerald-600', from: 'from-emerald-600', to: 'to-teal-800', light: 'bg-emerald-50', text: 'text-emerald-600', shadow: 'shadow-emerald-200' },
    { bg: 'bg-orange-600', from: 'from-orange-600', to: 'to-red-700', light: 'bg-orange-50', text: 'text-orange-600', shadow: 'shadow-orange-200' },
    { bg: 'bg-purple-600', from: 'from-purple-600', to: 'to-indigo-900', light: 'bg-purple-50', text: 'text-purple-600', shadow: 'shadow-purple-200' },
    { bg: 'bg-rose-600', from: 'from-rose-600', to: 'to-pink-800', light: 'bg-rose-50', text: 'text-rose-600', shadow: 'shadow-rose-200' },
    { bg: 'bg-amber-500', from: 'from-amber-500', to: 'to-orange-700', light: 'bg-amber-50', text: 'text-amber-600', shadow: 'shadow-amber-200' },
  ];

  if (!user || !user.id) return null;

  return (
    <DashboardLayout title="Overview">
      <div className="space-y-4 sm:space-y-6">

        {/* Welcome Hero - Reduced Size & Radius */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 sm:p-7 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-orange-500/10 rounded-full -mr-12 -mt-12 blur-[60px]"></div>
          
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white/10 rounded-full text-[9px] font-black uppercase tracking-[0.15em] backdrop-blur-md border border-white/10">
                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(249,115,22,0.6)]"></span>
                Student ID: {user.id.toString().padStart(5, '0')}
              </div>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight">
                Welcome Back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">{user.full_name.split(' ')[0]}!</span>
              </h2>
              <p className="text-slate-400 text-[11px] font-medium max-w-sm">
                You have {enrollments.length} active class sessions this month.
              </p>
            </div>
            
            <button onClick={() => navigate('/student/all-classes')} className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-black rounded-xl text-[10px] uppercase tracking-widest transition-all shadow-lg shadow-orange-900/30 self-start sm:self-center">
              Enroll New
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">

          {/* Main Content */}
          <div className="lg:col-span-8 space-y-6">
            <section className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-sm sm:text-base font-black text-slate-800 flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center text-white shadow-md shadow-orange-100">
                    <FaGraduationCap size={12} />
                  </div>
                  Active Months
                </h3>
                <button onClick={() => navigate('/student/courses')} className="text-[9px] font-black text-slate-400 hover:text-orange-500 uppercase tracking-widest transition-all border-b border-transparent hover:border-orange-500 pb-0.5">View All</button>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[1,2].map(i => <div key={i} className="h-28 bg-slate-50 rounded-xl animate-pulse border border-slate-100"></div>)}
                </div>
              ) : enrollments.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center border-2 border-dashed border-slate-100">
                  <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-200">
                    <FaGraduationCap size={24} />
                  </div>
                  <h4 className="text-slate-800 font-bold mb-1 text-xs">No active enrollments</h4>
                  <p className="text-slate-400 text-[10px] mb-4 max-w-[180px] mx-auto font-medium">Start your learning journey by enrolling today.</p>
                  <button onClick={() => navigate('/student/all-classes')} className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-black text-[9px] uppercase tracking-widest shadow-lg hover:bg-orange-500 transition-all">Browse Catalog</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {enrollments.slice(0, 4).map((en, idx) => {
                    const theme = COLOR_SCHEMES[idx % COLOR_SCHEMES.length];
                    return (
                      <div 
                        key={en.id} 
                        onClick={() => navigate(`/student/materials/${en.month_id}`)} 
                        className={`${theme.light} p-4 rounded-xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer group relative overflow-hidden`}
                      >
                        <div className={`absolute top-0 right-0 w-20 h-20 ${theme.bg} opacity-[0.05] rounded-full -mr-6 -mt-6 group-hover:scale-150 transition-transform duration-500`}></div>
                        
                        <div className="flex justify-between items-start mb-3 relative z-10">
                          <div className={`w-10 h-10 bg-white ${theme.text} rounded-lg flex items-center justify-center font-black text-xs group-hover:${theme.bg} group-hover:text-white transition-all shadow-sm`}>
                            {en.month_name.substring(0,3)}
                          </div>
                          <span className={`text-[8px] font-black uppercase ${theme.text} bg-white/90 px-2.5 py-1 rounded-full border border-white shadow-sm`}>Active</span>
                        </div>
                        
                        <div className="space-y-0.5 relative z-10">
                          <h4 className="font-black text-slate-800 text-xs line-clamp-1 group-hover:text-slate-900 transition-colors">{en.course_title}</h4>
                          <div className="flex items-center gap-2">
                            <p className={`text-[9px] font-black uppercase tracking-widest ${theme.text}`}>{en.month_name}</p>
                            <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Access Now</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Recent Payments Section */}
            <section className="space-y-3">
              <h3 className="text-sm sm:text-base font-black text-slate-800 flex items-center gap-2.5 px-1">
                <div className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center text-white shadow-md shadow-blue-100">
                  <FaWallet size={12} />
                </div>
                Payments
              </h3>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-[0.1em]">
                        <th className="px-5 py-3">Details</th>
                        <th className="px-5 py-3">Amount</th>
                        <th className="px-5 py-3 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {recentPayments.length === 0 ? (
                        <tr><td colSpan="3" className="px-5 py-8 text-center text-slate-400 italic text-[10px]">No recent payments</td></tr>
                      ) : (
                        recentPayments.map(p => (
                          <tr key={p.id} className="hover:bg-slate-50/50 transition-colors text-[11px]">
                            <td className="px-5 py-3">
                              <p className="font-black text-slate-700">{p.course_title}</p>
                              <p className="text-[9px] text-slate-400 font-bold uppercase">{p.month_name}</p>
                            </td>
                            <td className="px-5 py-3 font-black text-slate-800">LKR {p.amount}</td>
                            <td className="px-5 py-3 text-right">
                              <span className={`text-[8px] font-black uppercase px-2 py-1 rounded-lg border ${
                                p.status === 'approved' 
                                ? 'bg-green-50 text-green-600 border-green-100' 
                                : 'bg-orange-50 text-orange-600 border-orange-100'
                              }`}>
                                {p.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-5">
            <div className="bg-slate-900 rounded-2xl p-5 text-white shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500 opacity-20 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700"></div>
              
              <div className="relative z-10 space-y-4">
                <div>
                  <h4 className="font-black text-xs mb-0.5 uppercase tracking-wide">Support</h4>
                  <p className="text-slate-400 text-[10px] leading-relaxed font-medium">Need help with materials?</p>
                </div>
                <button 
                  onClick={() => setIsSupportOpen(true)}
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all shadow-lg shadow-orange-900/30 transform active:scale-95"
                >
                  Contact Support
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h4 className="font-black text-slate-800 text-[10px] uppercase tracking-widest mb-3">Updates</h4>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
                  <p className="text-[10px] text-slate-600 font-medium leading-relaxed">
                    Grade 12 Physics notes are updated.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-1 flex-shrink-0 animate-pulse"></div>
                  <p className="text-[10px] text-slate-600 font-medium leading-relaxed">
                    Live session today at 7:00 PM.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      
      {/* Support Popup Component */}
      <SupportPopup 
        isOpen={isSupportOpen} 
        onClose={() => setIsSupportOpen(false)} 
      />
    </DashboardLayout>
  );
};

export default StudentDashboard;
